/* ==========================================================================
   FILE: modules/identity/components/ProfileCard.jsx
   IAMONEAI - Profile Card Component
   Displays user identity information
   ========================================================================== */

import React, { useState } from 'react';
import { Button } from '../../../global/components';
import IINDisplay from './IINDisplay';
import './ProfileCard.css';

// ==========================================================================
// PROFILE CARD COMPONENT
// ==========================================================================

/**
 * User profile card showing identity information
 * 
 * @param {Object} props
 * @param {Object} user - User data (iin, firstName, lastName, email, etc.)
 * @param {Object} brain - Brain data (personality, contexts, etc.)
 * @param {Function} onEdit - Called when edit is requested
 * @param {Function} onLogout - Called when logout is requested
 */
function ProfileCard({
  user = {},
  brain = {},
  onEdit,
  onLogout,
  className = '',
}) {
  const [showFullIIN, setShowFullIIN] = useState(false);

  // Extract user info
  const {
    iin,
    firstName,
    lastName,
    email,
    wallet,
    birthDate,
    createdAt,
    blockchainAnchored,
  } = user;

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'User';
  const initials = getInitials(fullName);
  const memberSince = createdAt ? formatDate(createdAt) : null;
  const activeContext = brain?.activeContext || 'personal';
  const contextConfig = brain?.contexts?.[activeContext] || {};

  const cardClasses = [
    'profile-card',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      {/* Header with Avatar */}
      <div className="profile-card-header">
        <div className="profile-avatar">
          <span className="profile-avatar-initials">{initials}</span>
          {blockchainAnchored && (
            <span className="profile-avatar-verified" title="Blockchain Verified">
              ✓
            </span>
          )}
        </div>
        
        <div className="profile-info">
          <h2 className="profile-name">{fullName}</h2>
          {email && <p className="profile-email">{email}</p>}
        </div>

        {onEdit && (
          <button
            className="profile-edit-btn"
            onClick={onEdit}
            aria-label="Edit profile"
          >
            <EditIcon />
          </button>
        )}
      </div>

      {/* IIN Section */}
      {iin && (
        <div className="profile-section">
          <div className="profile-section-header">
            <span className="profile-section-label">Identity Number (IIN)</span>
            <button
              className="profile-toggle-btn"
              onClick={() => setShowFullIIN(!showFullIIN)}
            >
              {showFullIIN ? <EyeOffIcon /> : <EyeIcon />}
              {showFullIIN ? 'Hide' : 'Show'}
            </button>
          </div>
          <IINDisplay iin={iin} masked={!showFullIIN} copyable />
        </div>
      )}

      {/* Active Context */}
      <div className="profile-section">
        <span className="profile-section-label">Active Mode</span>
        <div 
          className="profile-context-badge"
          style={{ '--context-color': contextConfig.color || '#10B981' }}
        >
          <span className="profile-context-icon">{contextConfig.icon || '🏠'}</span>
          <span className="profile-context-name">{contextConfig.name || 'Personal'}</span>
          <span className="profile-context-style">{contextConfig.tone || 'friendly'}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="profile-stats">
        <ProfileStat
          icon="🧠"
          label="Memories"
          value={brain?.memoryCount || 0}
        />
        <ProfileStat
          icon="🎯"
          label="Goals"
          value={brain?.goalCount || 0}
        />
        <ProfileStat
          icon="📅"
          label="Member Since"
          value={memberSince || 'N/A'}
          small
        />
        <ProfileStat
          icon={blockchainAnchored ? '⛓️' : '🔗'}
          label="Blockchain"
          value={blockchainAnchored ? 'Verified' : 'Pending'}
          status={blockchainAnchored ? 'success' : 'warning'}
        />
      </div>

      {/* Wallet (if present) */}
      {wallet && (
        <div className="profile-section profile-wallet-section">
          <span className="profile-section-label">Wallet Address</span>
          <div className="profile-wallet">
            <code>{shortenWallet(wallet)}</code>
            <CopyButton text={wallet} />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="profile-card-actions">
        {onLogout && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="profile-logout-btn"
          >
            <LogoutIcon />
            Sign Out
          </Button>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// PROFILE STAT COMPONENT
// ==========================================================================

function ProfileStat({ icon, label, value, small = false, status }) {
  return (
    <div className={`profile-stat ${small ? 'profile-stat-small' : ''}`}>
      <span className="profile-stat-icon">{icon}</span>
      <div className="profile-stat-content">
        <span className={`profile-stat-value ${status ? `profile-stat-${status}` : ''}`}>
          {value}
        </span>
        <span className="profile-stat-label">{label}</span>
      </div>
    </div>
  );
}

// ==========================================================================
// COPY BUTTON COMPONENT
// ==========================================================================

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <button
      className="copy-btn"
      onClick={handleCopy}
      aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

// ==========================================================================
// HELPER FUNCTIONS
// ==========================================================================

function getInitials(name) {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

function shortenWallet(wallet) {
  if (!wallet || wallet.length < 12) return wallet;
  return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
}

// ==========================================================================
// ICONS
// ==========================================================================

function EditIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default ProfileCard;